import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslate } from '@tolgee/react';
import { useNavigate } from '@tanstack/react-router';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useGlobalLoader } from '~/context/GlobalLoaderContext';
import { LoadingSpinner } from '~/components/global/LoadingSpinner';
import { EmptyState } from '~/components/empty/EmptyState';
import { Button } from '~/components/ui/Button';

interface OAuthCallbackProps {
  // No props needed for this component currently
}

const RETRY_DELAY_MS = 1000;
const HUBSPOT_ID_TIMEOUT_MS = 30000; // 30 seconds

/**
 * Helper to extract state params (lang, companyId) from URL state parameter
 */
function parseStateParams(state: string | null): {
  lang: string;
  companyId: string;
} {
  let lang = 'de';
  let companyId = 'default';

  if (state) {
    const parts = state.split('/').filter(Boolean);
    if (parts.length >= 2) {
      lang = parts[0];
      companyId = parts[1];
    }
  }

  return { lang, companyId };
}

/**
 * Wait for a specified duration
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const OAuthCallback: React.FC<OAuthCallbackProps> = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { user, isLoaded, isSignedIn } = useUser();
  const clerk = useClerk();
  const { showLoader, hideLoader } = useGlobalLoader();

  const [
    isLoading,
    setIsLoading,
  ] = useState(true);
  const [
    error,
    setError,
  ] = useState<string | null>(null);
  const [
    timedOut,
    setTimedOut,
  ] = useState(false);

  const isMountedRef = useRef(true);
  const hubspotTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userReloadIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const goTo = useCallback(
    (to: string) => {
      if (isMountedRef.current) {
        void navigate({ to, replace: true });
      }
    },
    [navigate],
  );

  const getHubspotContactUrl = useCallback(
    (clerkUser: typeof user, state?: string | null): string => {
      const { lang, companyId } = parseStateParams(state);

      const hubspotContactId =
        clerkUser?.publicMetadata &&
        typeof clerkUser.publicMetadata === 'object' &&
        'hubspotContactId' in clerkUser.publicMetadata
          ? (clerkUser.publicMetadata as Record<string, unknown>)
              .hubspotContactId
          : null;

      if (hubspotContactId) {
        return `/${lang}/${companyId}/${String(hubspotContactId)}`;
      }
      return '';
    },
    [],
  );

  useEffect(() => {
    showLoader();
    return () => {
      hideLoader();
      if (hubspotTimeoutRef.current) clearTimeout(hubspotTimeoutRef.current);
      if (userReloadIntervalRef.current)
        clearInterval(userReloadIntervalRef.current);
      isMountedRef.current = false;
    };
  }, [
    showLoader,
    hideLoader,
  ]);

  useEffect(() => {
    const handleCallback = async (): Promise<void> => {
      if (!isMountedRef.current) return;

      setIsLoading(true);
      setError(null);
      setTimedOut(false);

      if (hubspotTimeoutRef.current) clearTimeout(hubspotTimeoutRef.current);
      if (userReloadIntervalRef.current)
        clearInterval(userReloadIntervalRef.current);

      try {
        await clerk.handleRedirectCallback();

        const urlParams = new URLSearchParams(window.location.search);
        const state = urlParams.get('state');

        let hubspotIdFound = false;
        let timeoutReached = false;

        const checkUserAndRedirect = async () => {
          if (!isMountedRef.current) return;

          if (!isLoaded || !isSignedIn) {
            try {
              await user?.reload();
            } catch (reloadError) {
              console.error(
                'Error reloading user data during initial wait:',
                reloadError,
              );
              setError('Fehler beim Aktualisieren der Benutzerdaten.');
              setTimedOut(true);
              cleanup();
              hideLoader();
              setIsLoading(false);
              return;
            }
            if (!isLoaded || !user || !isSignedIn) return;
          }

          const currentHubspotUrl = getHubspotContactUrl(user, state);

          if (currentHubspotUrl) {
            hubspotIdFound = true;
            cleanup();
            hideLoader();
            setIsLoading(false);
            console.log(
              `HubSpot ID found. Redirecting to: ${currentHubspotUrl}`,
            );
            goTo(currentHubspotUrl);
            return;
          }

          if (timeoutReached && !hubspotIdFound) {
            cleanup();
            hideLoader();
            setIsLoading(false);
            setTimedOut(true);
            setError(
              'Ihr Konto konnte nicht rechtzeitig vorbereitet werden. Bitte laden Sie die Seite neu oder versuchen Sie es später erneut.',
            );
            console.error('HubSpot ID timeout reached.');
            return;
          }

          if (isLoaded && isSignedIn && user) {
            try {
              await user.reload();
            } catch (reloadError) {
              console.error(
                'Error reloading user data in interval:',
                reloadError,
              );
            }
          }
        };

        hubspotTimeoutRef.current = setTimeout(() => {
          timeoutReached = true;
          console.warn('HubSpot ID timeout reached.');
          if (isMountedRef.current) {
            checkUserAndRedirect();
          }
        }, HUBSPOT_ID_TIMEOUT_MS);

        await checkUserAndRedirect();

        if (!hubspotIdFound && !timeoutReached && isMountedRef.current) {
          userReloadIntervalRef.current = setInterval(
            checkUserAndRedirect,
            RETRY_DELAY_MS,
          );
        }
      } catch (callbackError: unknown) {
        console.error('OAuth callback handling failed:', callbackError);
        setError('Fehler bei der Verarbeitung des OAuth-Callbacks.');
        if (isMountedRef.current) {
          setTimedOut(true);
          hideLoader();
          setIsLoading(false);
        }
        const urlParams = new URLSearchParams(window.location.search);
        const state = urlParams.get('state');
        const { lang, companyId } = parseStateParams(state);
        goTo(`/${lang}/${companyId}/public/auth/login?sso_error=true`);
      }
    };

    if (isLoaded) {
      void handleCallback();
    }

    const cleanup = () => {
      if (hubspotTimeoutRef.current) clearTimeout(hubspotTimeoutRef.current);
      if (userReloadIntervalRef.current)
        clearInterval(userReloadIntervalRef.current);
      isMountedRef.current = false;
    };

    return cleanup;
  }, [
    isLoaded,
    clerk,
    navigate,
    getHubspotContactUrl,
    hideLoader,
    showLoader,
    goTo,
    user,
    isSignedIn,
  ]);

  if (error || timedOut) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <EmptyState
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-6 w-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
          }
          title={timedOut ? t('oauth_error_timeout') : t('oauth_error_title')}
          description={error || t('oauth_error_unexpected')}
          actions={
            <Button
              primary
              onClick={() => window.location.reload()}
            >
              {t('oauth_callback_reload_page')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4">
      <LoadingSpinner />
      <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        {t('oauth_callback_preparing_account')}
      </p>
    </div>
  );
};

export const OAuthSuccessPage: React.FC = () => {
  const { t } = useTranslate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-white">
            {t('oauth_success_title')}
          </h2>
          <p className="mt-2 text-sm text-gray-300">
            {t('oauth_success_description')}
          </p>
        </div>
      </div>
    </div>
  );
};

export const OAuthFailurePage: React.FC = () => {
  const { t } = useTranslate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-white">
            {t('oauth_failure_title')}
          </h2>
          <p className="mt-2 text-sm text-gray-300">
            {t('oauth_failure_message')}
          </p>
          <div className="mt-6">
            <a
              href="/login"
              className="text-sm font-medium text-indigo-400 hover:text-indigo-300"
            >
              {t('oauth_back_to_login')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
